import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      nav(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (er) {
      setErr(formatApiError(er));
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1579119134757-5c38803f34fc?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Volunteers with a rescue dog" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ink)]/80 via-[color:var(--ink)]/25 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display text-4xl leading-tight max-w-md">Every wagging tail starts with a volunteer.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[color:var(--cream)]">
        <div className="w-full max-w-md">
          <a href="https://hoodriveradoptadog.org" target="_blank" rel="noopener noreferrer" data-testid="login-logo-link" className="inline-block mb-8">
            <img src="/logo.png" alt="Hood River Adopt A Dog" className="h-14 w-auto" />
          </a>

          <h1 className="font-display text-4xl mb-2">Welcome back.</h1>
          <p className="text-[color:var(--stone)] mb-6">Sign in to see your shifts and notifications.</p>

          <button data-testid="login-google-btn" onClick={googleSignIn} className="w-full btn-pill btn-ghost-ink flex items-center justify-center gap-3 mb-4">
            <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-4 text-center text-xs text-[color:var(--stone)]">or</div>

          <form onSubmit={submit} className="space-y-3">
            <input data-testid="login-email-input" type="email" required autoFocus placeholder="Email" value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            <input data-testid="login-password-input" type="password" required placeholder="Password" value={password} onChange={(e)=>setPassword(e.target.value)} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            {err && <div data-testid="login-error" className="text-sm text-[color:var(--red)]">{err}</div>}
            <button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full btn-pill btn-primary disabled:opacity-60">{busy ? "Signing in…" : "Sign in"}</button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link data-testid="login-forgot-link" to="/forgot-password" className="text-[color:var(--stone)] hover:text-[color:var(--ink)]">Forgot password?</Link>
            <Link data-testid="login-register-link" to="/register" className="font-semibold text-[color:var(--red)]">Create account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
